<template>
  <div class="tw:pt-4">
    <div class="inner tw:py-0">
      <h1>Carrier Group Mapping</h1>
    </div>

    <div class="inner tw:flex tw:items-center tw:justify-between tw:gap-4 tw:py-2">
      <div class="tw:flex tw:items-center tw:gap-4">
        <div class="tw:join tw:justify-center">
          <input type="radio" name="carrierOption" aria-label="Issuing Companies"
                 class="tw:join-item tw:btn tw:btn-sm"
                 @click="mode = 'issuingCompanies'"
                 :checked="mode === 'issuingCompanies'" />

          <input type="radio" name="carrierOption" aria-label="Carriers"
                 class="tw:join-item tw:btn tw:btn-sm"
                 @click="mode = 'carriers'"
                 :checked="mode === 'carriers'" />
        </div>

        <CreateCarrierModal v-if="mode === 'carriers'" @created="carrierCreated" />
      </div>

      <label class="search-bar tw:input tw:input-sm tw:flex tw:min-w-96 tw:items-center tw:gap-2">
        <input
          ref="searchInputRef"
          type="text"
          placeholder="Search"
          class="tw:grow"
          v-model="filterText"
          autocomplete="off"
          @keyup="handleSearchKeyUp" />
        <XIcon
          v-if="filterText && filterText.length > 0"
          :size="20"
          class="tw:cursor-pointer"
          @click="filterText = ''" />
        <SearchIcon :size="20" />
      </label>
    </div>

    <div class="inner tw:pt-0">

    <v-alert v-if="!loadingData && error" border="top" color="error-lighten-2">{{ error }}</v-alert>

    <div v-visible="isError" class="tw:toast tw:toast-top tw:toast-center tw:z-2000">
      <div class="tw:alert tw:bg-primary tw:text-white">
        <span>{{ errorMessage }}</span>
        <app-button @click="errorMessage = undefined" class="tw:text-xs">OK</app-button>
      </div>
    </div>

    <v-skeleton-loader type="table-tbody" v-if="loadingData && !error"/>
    <v-row no-gutters v-else>

      <table v-if="mode === 'issuingCompanies'" v-tablesort id="issuing-companies-table" class="apptable dense mt-2">
        <thead>
        <tr>
          <td class="text-left sorted ascending">Issuing Company</td>
          <td class="country">Carrier Group</td>
          <td>AM Best Company</td>
          <td>AM Best Parent Company</td>
          <td class="nosort"></td>
        </tr>
        </thead>

        <tbody>
        <tr v-for="ic in filteredIssuingCompanyList" :key="ic.issuingCompanyId">
          <td>{{ ic.issuingCompanyName }}</td>
          <td>{{ ic.insurerGroup }}</td>
          <td>{{ ic.amBestCompanyName }}</td>
          <td>{{ ic.ultimateParentName }}</td>
          <td>
            <div class="tw:flex tw:justify-end">
              <app-button class="tw:rounded-full" @click="editIssuingCompany(ic)">
                <Edit2 :size="16" />
              </app-button>
            </div>
          </td>
        </tr>
        </tbody>
      </table>

      <table v-else-if="mode === 'carriers'" v-tablesort id="carriers-table" class="apptable dense mt-2">
        <thead>
        <tr>
          <td>Carrier</td>
          <td>Short Name</td>
          <td class="nosort"></td>
        </tr>
        </thead>

        <tbody>
        <tr v-for="c in filteredCarrierList" :key="c.ID">
          <td>{{ c.name }}</td>
          <td>{{ c.shortName }}</td>
          <td>
            <div class="tw:flex tw:justify-end">
              <app-button class="tw:rounded-full" @click="editCarrier(c)">
                <Edit2 :size="16" />
              </app-button>
            </div>
          </td>
        </tr>
        </tbody>
      </table>
    </v-row>
    </div>

    <v-dialog persistent :transition="false" v-model="showDialog" scrollable width="600px">
      <v-card>
        <v-card-title><span class="text-h5">Editing {{ editingIssuingCompany ? selected.issuingCompanyName : selected.name }}</span></v-card-title>
        <v-card-text>
          <div v-if="selected" class="mt-4 ml-2">
            <v-combobox
              v-if="editingIssuingCompany"
                variant="outlined" 
                density="compact"
                label="Carrier Group"
                v-model="selected.insurerGroup"
                :items="carriers"
                data-test="carrier-group-combo"/>

            <v-text-field
              v-if="editingCarrier"
              variant="outlined" density="compact"
              label="Carrier Name"
              v-model="rename"
              data-test="rename-group-input"
              :error-messages="editingCarrierError"
              autofocus
            />

            <v-text-field
              v-if="editingCarrier"
              variant="outlined" density="compact"
              label="Short Name"
              v-model="renameShort"
              data-test="rename-group-input"
              :error-messages="editingCarrierShortNameError"
              autofocus
            />

            <v-combobox
              v-if="editingIssuingCompany"
                variant="outlined"
                density="compact"
                label="AM Company Name"
                v-model="selected.amBestCompanyName"
                :items="amBestCompanyNames"
                data-test="ultimate-parent-group-combo"/>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn variant="flat" size="small" :disabled="saving" @click="closeDialog" data-test="cancel-action">Cancel</v-btn>
          <v-btn variant="flat" size="small" color="primary" data-test="confirm-action" @click="() => {onMapSave()}" :loading="saving" :disabled="saving">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { carrierService } from '@/services/api';
import { adminService } from '@/services/AdminService';
import CreateCarrierModal from '@/components/CreateCarrierModal';
import AppButton from '@/components/v2/AppButton.vue';
import { Edit2, SearchIcon, XIcon } from 'lucide-vue-next';

const IssuingCompanyNameKey = 'issuingCompanyName',
    InsurerGroupKey = 'insurerGroup';

export default {
  name: 'CarrierGroupMapping',
  components: {
    AppButton,
    CreateCarrierModal,
    Edit2,
    SearchIcon,
    XIcon
  },
  data: () => ({
    mode: 'issuingCompanies',
    loadingData: true,
    editingIssuingCompany: false,
    editingCarrier: false,
    editingCarrierError: '',
    editingCarrierShortNameError: '',
    showTable: true,
    saving: false,
    error: undefined,
    filterText: '',
    list: [],
    carrierList: [],
    carriers: [],
    amBestCompanies: [],
    selected: undefined,
    selectedID: undefined,
    rename: '',
    renameShort: '',
    originalGroup: '',
    originalGroupShortName: '',
    errorMessage: undefined,
    issuingCompanyNameKey: IssuingCompanyNameKey,
    insurerGroupKey: InsurerGroupKey,
    sortAsc: true,
    sortKey: IssuingCompanyNameKey,
    dialog: false,
    amBestParents: [],
    showSaveError: false,
    saveError: ''
  }),
  computed: {
    showDialog() {
      return this.editingIssuingCompany || this.editingCarrier;
    },
    amBestCompanyNames() {
      return this.amBestCompanies.map(c => c.ambCompanyName).sort((c1, c2) => c1.localeCompare(c2));
    },
    isError() {
      return this.errorMessage !== '' && this.errorMessage !== undefined;
    },
    filteredIssuingCompanyList() {
      let result = this.list;

      if (this.filterText) {
        result = result.filter(item => {
          for (const part of this.filterText.toLowerCase().split(' ')) {
            if (item.issuingCompanyName.toLowerCase().includes(part)) {
              return true;
            }
          }
          return false;
        });
      }

      // Sort so that undefined carrier groups are at the top
      return [...result].sort((a, b) => {
        const aUndefined = this.isUndefinedCarrierGroup(a.insurerGroup);
        const bUndefined = this.isUndefinedCarrierGroup(b.insurerGroup);
        if (aUndefined && !bUndefined) return -1;
        if (!aUndefined && bUndefined) return 1;
        return 0;
      });
    },
    filteredCarrierList() {
      if (!this.filterText) {
        return this.carrierList;
      }

      return this.carrierList.filter(item => {
        for (const part of this.filterText.toLowerCase().split(' ')) {
          if (item.name.toLowerCase().includes(part)) {
            return true;
          }
        }
        return false;
      });
    }
  },
  methods: {
    isUndefinedCarrierGroup(carrierGroup) {
      return carrierGroup === 'Undefined';
    },
    editIssuingCompany(ic) {
      this.editingIssuingCompany = true;
      this.editingCarrier = false;
      this.selected = { ...ic };
      this.selectedID = ic.issuingCompanyId;
      this.originalGroup = ic.insurerGroup;
      this.originalGroupShortName = ic.insurerGroupShortName;
    },
    editCarrier(c) {
      this.editingIssuingCompany = false;
      this.editingCarrier = true;
      this.selected = { ...c };
      this.selectedID = c.issuingCompanyId;
      this.rename = c.name;
      this.renameShort = c.shortName;
    },
    async closeDialog() {
      this.editingCarrier = false;
      this.editingIssuingCompany = false;
    },
    async refreshData() {
      this.loadingData = true;
      const [carriers, amBestCompanies, amBestParents, list] = await Promise.all([
        carrierService.list(),
        adminService.fetchAMBestCompanies(),
        adminService.fetchAMBestParents(),
        adminService.LoadCarrierIssuerList()
      ]);

      this.carriers = (carriers || []).map(item => item.name);
      this.carrierList = (carriers || []);
      this.list = list || [];
      this.amBestParents = amBestParents || [];
      this.amBestCompanies = amBestCompanies || [];

      this.sort(this.sortKey, this.sortAsc);
      this.loadingData = false;
    },
    onMapSave() {
      this.saving = true;
      setTimeout(async () => {

        if (this.editingCarrier) {
          const carrierInput = this.rename.trim();

          if (this.selected.name !== carrierInput) {
            const existing = this.carriers.find(item => item === carrierInput);
            if (existing) {
              this.editingCarrierError = 'Cannot rename to an existing Carrier Group';
              this.saving = false;
              return
            }
          }

          if (carrierInput === '') {
            this.editingCarrierError = 'Name cannot be empty';
            this.saving = false;
            return;
          }

          if (this.renameShort && this.renameShort.trim().length > 30) {
            this.editingCarrierShortNameError = 'Short Name must be 30 characters or less.';
            this.saving = false;
            return;
          }

          const req = {
            insurerGroupName: this.rename,
            insurerGroupShortName: this.renameShort?.trim()
          };

          await adminService.updateCarrier(this.selected.ID, req)

          this.saving = false;
          this.editingCarrier = false;

          await this.refreshData();

          return;
        }

        if (this.editingIssuingCompany) {
          try {

            let insurerGroupId = this.carrierList.find(c => c.name === this.selected.insurerGroup)?.ID;

            // if insurer group doesn't exist, create a new one

            if (!insurerGroupId) {
              insurerGroupId = await adminService.CreateCarrier({
                insurerGroupName: this.selected.insurerGroup.trim(),
                issuingCompanyId: this.selected.issuingCompanyId
              });
            }

            const req = {
              insurerGroupId: insurerGroupId,
              amBestNumber: this.amBestCompanies.find(c => c.ambCompanyName === this.selected.amBestCompanyName)?.ambNumber
            };

            await adminService.updateIssuingCompany(this.selected.issuingCompanyId, req);
            await this.refreshData();

            this.selected = {};
            this.rename = '';
            this.renameShort = '';

            this.editingIssuingCompany = false;
          } catch (err) {
            console.error(err);
            this.setErrorMessage('Unable to make this change');
          }
          this.saving = false;
        }
      }, 10);
    },
    setErrorMessage(msg) {
      this.rename = '';
      this.errorMessage = msg;
      setTimeout(() => {
        this.errorMessage = undefined;
      }, 4000);
    },
    handleSearchKeyUp(e) {
      if (e.key && e.key === 'Escape') {
        this.filterText = '';
        e.stopPropagation();
      }
    },
    sort(key, dir) {
      this.list.sort((a, b) => {
        const x = a[key].localeCompare(b[key]);
        return dir ? x : 0 - x;
      });
      this.sortKey = key;
      this.sortAsc = dir;
    },
    async carrierCreated(isError) {
      if (isError) {
        this.showSaveError = true;
        this.saveError = 'An unexpected error occurred.';
      } else {
        await this.refreshData();
      }
    },
  },
  mounted() {
    if (this.$refs.searchInputRef) {
      this.$refs.searchInputRef.focus();
    }
  },
  created() {
    this.refreshData();
  }
};
</script>

<style scoped>
.carrier-table-row > td > button {
  visibility: hidden;
}

.carrier-table-row:hover > td > button {
  visibility: visible;
}
</style>
